import React, { useEffect, useState, useRef } from "react";
import "./UserProfile.scss";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button";
import Modal from "../../components/Modal/Modal";
import ChangePassword from "../../components/ChangePassword/ChangePassword";
import { clearUser } from "../../state/auth/authSlice";
import { useDispatch } from "react-redux";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

function UserProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [userData, setUserData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [islogoutOpen, setIslogoutOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await axios.get(`${BE_URL}/profile`, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });
      setUserData(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          fetchUserData();
        } else {
          handleLogout();
        }
      } else {
        console.error("Failed to fetch user data:", error);
        setError("Failed to load profile data. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await axios.post(`${BE_URL}/refreshToken`, {
        refreshToken: tokens.refreshToken,
      });
      if (response.data.accessToken) {
        localStorage.setItem("ERPTokens", JSON.stringify(response.data));
        return true;
      } else {
        throw new Error("Failed to refresh token.");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      localStorage.removeItem("ERPTokens");
      dispatch(clearUser());
      navigate("/login");
      return false;
    }
  };

  // const handleFileChange = (fileInfo) => {
  //   if (fileInfo.length > 0) {
  //     const lastFile = fileInfo[fileInfo.length - 1];
  //     setSelectedFile(lastFile.blobFile);

  //     // Create preview URL
  //     const previewUrl = URL.createObjectURL(lastFile.blobFile);
  //     setUploadPreview(previewUrl);
  //   }
  // };

  const handleProfilePicUpload = async () => {
    if (selectedFile) {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append("profilePic", selectedFile);

      try {
        const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
        await axios.post(`${BE_URL}/upload-profile-pic`, formData, {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        });

        // Clean up preview URL
        if (uploadPreview) {
          URL.revokeObjectURL(uploadPreview);
          setUploadPreview(null);
        }

        setSelectedFile(null);
        fetchUserData();
      } catch (error) {
        console.error("Error uploading profile picture:", error);
        setError("Failed to upload profile picture. Please try again.");
      } finally {
        setUploadLoading(false);
      }
    }
  };

  const handleCancel = () => {
    if (uploadPreview) {
      URL.revokeObjectURL(uploadPreview);
      setUploadPreview(null);
    }
    setSelectedFile(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("ERPTokens");
    dispatch(clearUser());
    navigate("/login");
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Format role for display
  const formatRole = (roleId) => {
    if (!roleId) return "User";

    const roles = {
      1: "Administrator",
      5: "Teacher",
      10: "Student",
    };

    return roles[roleId] || "User";
  };

  if (isLoading) {
    return (
      <div className="profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Modals */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <ChangePassword onDone={closeModal} />
      </Modal>

      <Modal isOpen={islogoutOpen} onClose={() => setIslogoutOpen(false)}>
        <ConfirmationModal
          onConfirm={handleLogout}
          onCancel={() => setIslogoutOpen(false)}
          ques="Are you sure you want to logout?"
        />
      </Modal>

      <div className="profile">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p>Manage your profile information and account settings</p>
        </div>

        {error && (
          <div className="profile-error">
            <p>{error}</p>
            <button onClick={fetchUserData}>Try Again</button>
          </div>
        )}

        <div className="profile-content">
          <div className="profile-sidebar">
            <div className="profile-photo-section">
              <div className="profile-photo-container">
                {uploadPreview ? (
                  <img
                    src={uploadPreview}
                    alt="Preview"
                    className="profile-photo"
                  />
                ) : userData?.image_url ? (
                  <img
                    src={userData.image_url}
                    alt="Profile"
                    className="profile-photo"
                  />
                ) : (
                  <div className="profile-photo-placeholder">
                    <span>{userData?.name?.charAt(0) || "U"}</span>
                  </div>
                )}

                <button
                  className="change-photo-button"
                  onClick={triggerFileInput}
                >
                  <span className="icon">📷</span>
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      setSelectedFile(e.target.files[0]);
                      setUploadPreview(URL.createObjectURL(e.target.files[0]));
                    }
                  }}
                  accept="image/*"
                  style={{ display: "none" }}
                />
              </div>

              {selectedFile && (
                <div className="upload-actions">
                  <Button
                    text={uploadLoading ? "Uploading..." : "Upload Photo"}
                    onClick={handleProfilePicUpload}
                    disabled={uploadLoading}
                  />
                  <button className="cancel-upload" onClick={handleCancel}>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="profile-actions">
              <Button text="Change Password" type="arrow" onClick={openModal} />
              <Button
                text="Logout"
                type="red"
                onClick={() => setIslogoutOpen(true)}
              />
            </div>
          </div>

          <div className="profile-details">
            <div className="profile-card">
              <h2>Personal Information</h2>

              <div className="profile-info-group">
                <div className="profile-info-row">
                  <div className="info-label">Full Name</div>
                  <div className="info-value">
                    {userData?.name || "Not available"}
                  </div>
                </div>

                <div className="profile-info-row">
                  <div className="info-label">Email Address</div>
                  <div className="info-value">
                    {userData?.email || "Not available"}
                  </div>
                </div>

                <div className="profile-info-row">
                  <div className="info-label">Phone Number</div>
                  <div className="info-value">
                    {userData?.phone_number || "Not available"}
                  </div>
                </div>

                <div className="profile-info-row">
                  <div className="info-label">Role</div>
                  <div className="info-value">
                    <span className="role-badge">
                      {formatRole(userData?.role_id)}
                    </span>
                  </div>
                </div>

                {userData?.department && (
                  <div className="profile-info-row">
                    <div className="info-label">Department</div>
                    <div className="info-value">{userData.department}</div>
                  </div>
                )}

                {userData?.employee_id && (
                  <div className="profile-info-row">
                    <div className="info-label">Employee ID</div>
                    <div className="info-value">{userData.employee_id}</div>
                  </div>
                )}

                {userData?.student_id && (
                  <div className="profile-info-row">
                    <div className="info-label">Student ID</div>
                    <div className="info-value">{userData.student_id}</div>
                  </div>
                )}
              </div>
            </div>

            {userData?.address && (
              <div className="profile-card">
                <h2>Address Information</h2>

                <div className="profile-info-group">
                  <div className="profile-info-row">
                    <div className="info-label">Address</div>
                    <div className="info-value">{userData.address}</div>
                  </div>

                  {userData?.city && (
                    <div className="profile-info-row">
                      <div className="info-label">City</div>
                      <div className="info-value">{userData.city}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="profile-card account-section">
              <h2>Account Management</h2>
              <p>Manage your account settings, change password, or logout.</p>

              <div className="account-actions">
                <button className="account-action-link" onClick={openModal}>
                  <span className="icon">🔒</span>
                  Change Password
                </button>

                <button
                  className="account-action-link"
                  onClick={() => setIslogoutOpen(true)}
                >
                  <span className="icon">🚪</span>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ConfirmationModal = ({ onConfirm, onCancel, ques }) => {
  return (
    <div className="confirmation-modal">
      <div className="confirmation-icon">
        <span>❓</span>
      </div>
      <h3 className="confirmation-title">Confirm Action</h3>
      <p className="confirmation-message">{ques}</p>
      <div className="confirmation-actions">
        <Button text="Yes, Continue" onClick={onConfirm} type="red" />
        <Button text="Cancel" onClick={onCancel} type="arrow" />
      </div>
    </div>
  );
};

export default UserProfile;
